import { useCallback, useState } from 'react';
import { Modal } from 'reactstrap';
import { ROLE } from '../../../../shared/constants/common';
import { followPPV } from '../../../../shared/actions/followAction';
import { showError } from '../../../../shared/utils/swalHandler';
import { DEFAULT_AVATAR } from '../../../../shared/utils/matrix/matrixRoomUtils';
import { formatHandle } from '../call/utils/callFormatUtils';

/**
 * Shown when a subscriber taps voice/video while the chat is locked (unfollowed / unsubscribed).
 * Subscribe flow is opened by the parent via onSubscribeClick (sibling SubscribeModalLoggedIn + openSignal).
 */
const ChatUnlockForCallModal = ({
  isOpen,
  onClose,
  creatorInfo,
  onFollowSuccess,
  onSubscribeClick,
  callKind = 'voice',
}) => {
  const [followLoading, setFollowLoading] = useState(false);

  const creatorRole = creatorInfo?.rol;
  const needsSubscribe = creatorRole === ROLE.CREATOR_SUB;

  const handleFollow = useCallback(async () => {
    const id = creatorInfo?._id;
    if (!id) return;

    setFollowLoading(true);
    try {
      const response = await followPPV(id);
      if (response?.success) {
        onClose?.();
        onFollowSuccess?.();
      } else {
        showError('Error', response?.error || 'Something went wrong. Please try again.');
      }
    } catch {
      showError('Error', 'Something went wrong. Please try again.');
    } finally {
      setFollowLoading(false);
    }
  }, [creatorInfo?._id, onClose, onFollowSuccess]);

  const avatarSrc =
    typeof creatorInfo?.profile_picture === 'string' && creatorInfo.profile_picture.trim() !== ''
      ? creatorInfo.profile_picture
      : DEFAULT_AVATAR;
  const handle = formatHandle(creatorInfo?.username);

  return (
    <Modal
      isOpen={isOpen}
      centered
      className="cm_call_info_modal chat_unlock_for_call_modal"
      toggle={onClose}
    >
      <div className="cm_call_info_header d-flex align-items-center mb-3">
        <div className="m_p_icon">
          <i
            className={callKind === 'video' ? 'fa-solid fa-video' : 'fa-solid fa-phone'}
            aria-hidden
          />
        </div>
        <h5 className="m-0">{callKind === 'voice' ? 'Voice Calls Locked' : 'Video Calls Locked'}</h5>
        <span
          className="mm_close_btn"
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose?.();
            }
          }}
        >
          <i className="fa-solid fa-xmark" aria-hidden />
        </span>
      </div>

      <div className="d-flex flex-column align-items-center text-center">
        <img src={avatarSrc} className="pp_img flex-shrink-0 mb-2" alt="" />
        <div className="fw-bold text_truncate w-100">{handle}</div>
        <p className="mt-3 mb-4 px-1">
          {needsSubscribe
            ? 'Subscribe to request voice or video calls with this creator.'
            : 'Follow this creator to request voice or video calls.'}
        </p>

        {needsSubscribe ? (
          <button
            type="button"
            className="jh_btn_pink w-100"
            onClick={() => {
              onClose?.();
              onSubscribeClick?.();
            }}
          >
            Subscribe
          </button>
        ) : (
          <button
            type="button"
            className="jh_btn_pink w-100"
            onClick={handleFollow}
            disabled={followLoading}
          >
            {followLoading ? (
              <span>
                <i className="fa-solid fa-spinner fa-spin me-2" aria-hidden />
                Following…
              </span>
            ) : (
              'Follow'
            )}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default ChatUnlockForCallModal;
